import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { StarIcon, CheckIcon, DrumstickIcon, FireIcon, SparkleIcon, LocationIcon } from '../components/Icons'
import './Careers.css'

const positions = [
  { id: 1, title: 'Store Manager', location: 'Multiple Locations', type: 'Full-time', department: 'Operations' },
  { id: 2, title: 'Kitchen Staff', location: 'Bangalore', type: 'Full-time', department: 'Operations' },
  { id: 3, title: 'Delivery Partner', location: 'Multiple Locations', type: 'Part-time', department: 'Operations' },
  { id: 4, title: 'Marketing Executive', location: 'Bangalore HQ', type: 'Full-time', department: 'Marketing' },
  { id: 5, title: 'HR Coordinator', location: 'Bangalore HQ', type: 'Full-time', department: 'Human Resources' },
  { id: 6, title: 'Supply Chain Executive', location: 'Bangalore HQ', type: 'Full-time', department: 'Operations' },
]

const benefits = [
  { icon: <StarIcon size={30} color="#FFD700" />, title: 'Competitive Pay', description: 'Industry-leading salaries and performance bonuses' },
  { icon: <CheckIcon size={30} />, title: 'Growth Opportunities', description: 'Clear career progression paths' },
  { icon: <CheckIcon size={30} />, title: 'Health Benefits', description: 'Comprehensive medical coverage' },
  { icon: <DrumstickIcon size={30} />, title: 'Free Meals', description: 'Enjoy BIGGUYS meals on duty' },
  { icon: <CheckIcon size={30} />, title: 'Training', description: 'Continuous learning and development' },
  { icon: <StarIcon size={30} color="#E4002B" />, title: 'Great Culture', description: 'Work with passionate food lovers' },
]

export default function Careers() {
  const pageRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.careers-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Culture values animation
      gsap.fromTo('.culture-item',
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.culture-section',
            start: 'top 70%',
          }
        }
      )

      // Benefits animation
      gsap.fromTo('.benefit-item',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.08,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.benefits-grid',
            start: 'top 75%',
          }
        }
      )

      // Job cards animation
      gsap.fromTo('.job-card',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.jobs-section',
            start: 'top 70%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div className="careers-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="careers-hero">
        <div className="container">
          <div className="careers-hero-content">
            <span className="hero-label">We're Hiring</span>
            <h1 className="hero-title">
              Join the
              <span className="highlight"> BIGGUYS Family</span>
            </h1>
            <p className="hero-description">
              Be part of India's fastest-growing QSR brand. We're looking for passionate 
              people who love food and want to make a difference.
            </p>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="culture-section">
        <div className="container">
          <div className="culture-grid">
            <div className="culture-content">
              <span className="section-label">Our Culture</span>
              <h2 className="section-title">Bold Flavours, Bolder People</h2>
              <p>
                At BIGGUYS, we believe our team is our greatest asset. We foster a culture 
                of respect, hustle, and team spirit where everyone has the opportunity to grow.
              </p>
            </div>
            <div className="culture-values">
              <div className="culture-item">
                <FireIcon size={30} className="culture-icon" />
                <span className="culture-text">Bold Flavours, Bolder People</span>
              </div>
              <div className="culture-item">
                <SparkleIcon size={25} className="culture-icon" />
                <span className="culture-text">Clean Kitchen, Clean Culture</span>
              </div>
              <div className="culture-item">
                <StarIcon size={25} color="#E4002B" className="culture-icon" />
                <span className="culture-text">Respect. Hustle. Team Spirit.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Perks & Benefits</span>
            <h2 className="section-title">Why Work With Us?</h2>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div className="benefit-item" key={index}>
                <span className="benefit-icon">{benefit.icon}</span>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="jobs-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Open Positions</span>
            <h2 className="section-title">Current Opportunities</h2>
          </div>

          <div className="jobs-grid">
            {positions.map((job) => (
              <div className="job-card" key={job.id}>
                <div className="job-header">
                  <h3 className="job-title">{job.title}</h3>
                  <span className="job-type">{job.type}</span>
                </div>
                <div className="job-details">
                  <span className="job-location">
                    <LocationIcon size={16} className="inline-icon" />
                    {job.location}
                  </span>
                  <span className="job-department">{job.department}</span>
                </div>
                <a href={`mailto:franchise@bigguys.in?subject=Application for ${job.title}`} className="btn btn-secondary btn-sm">
                  Apply Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="careers-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Don't See a Suitable Position?</h2>
            <p>Send us your resume and we'll keep you in mind for future opportunities.</p>
            <a href="mailto:franchise@bigguys.in?subject=General Application" className="btn btn-primary">
              Send Resume
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
